import { useRef, type ReactNode } from "react";
import {
  ArrowRight,
  History,
  Plus,
  Send,
  Sparkles,
  Square,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface AgentHeroProps {
  /** Label affiché sous "Agent" (ex: "entreprises", "dev", "calendrier"). */
  label: string;
  /** Texte d'introduction sous le label. */
  description: ReactNode;
  /** Valeur courante du textarea. */
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onStop?: () => void;
  /** Streaming en cours → bouton Stop + textarea désactivée. */
  isStreaming?: boolean;
  /** Prompts suggérés affichés tant qu'aucun message n'a été envoyé. */
  suggestedPrompts?: string[];
  onSuggestedClick?: (prompt: string) => void;
  /** Affiche/masque le bouton historique. */
  onOpenHistory?: () => void;
  isHistoryOpen?: boolean;
  onNewConversation?: () => void;
  /** Si fourni, rendu en zone "historique" sous le hero (collapsible). */
  historySlot?: ReactNode;
  /** Visible uniquement quand aucun message dans le thread. */
  showSuggestions?: boolean;
  className?: string;
}

export function AgentHero({
  label,
  description,
  input,
  onInputChange,
  onSubmit,
  onStop,
  isStreaming = false,
  suggestedPrompts = [],
  onSuggestedClick,
  onOpenHistory,
  isHistoryOpen = false,
  onNewConversation,
  historySlot,
  showSuggestions = true,
  className,
}: AgentHeroProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isStreaming) return;
    onSubmit();
  };

  return (
    <div
      className={cn(
        "rounded-fabrik-card border border-border bg-fabrik-green-soft/40 dark:bg-fabrik-green-soft px-4 py-4",
        className,
      )}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="size-9 shrink-0 rounded-lg bg-fabrik-green-soft text-fabrik-accent-text flex items-center justify-center">
          <Sparkles className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-fabrik-accent-text mb-0.5">
            Agent {label}
          </div>
          <p className="text-[13px] text-foreground/90 leading-relaxed">
            {description}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onOpenHistory && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onOpenHistory}
              className="gap-1.5 h-7 px-2 text-xs"
              aria-pressed={isHistoryOpen}
            >
              <History className="size-3" />
              Historique
            </Button>
          )}
          {onNewConversation && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onNewConversation}
              className="gap-1.5 h-7 px-2 text-xs"
            >
              <Plus className="size-3" />
              Nouvelle
            </Button>
          )}
        </div>
      </div>

      {historySlot && isHistoryOpen && (
        <div className="border-t border-border pt-3 mt-2 max-h-56 overflow-y-auto">
          {historySlot}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-3">
        <div className="flex items-end gap-2 rounded-lg bg-background border border-border focus-within:border-fabrik-green px-3 py-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={`Pose une question à propos de ${label}…`}
            rows={1}
            disabled={isStreaming}
            className="flex-1 min-h-0 h-auto resize-none bg-transparent border-0 p-0 text-[13px] focus-visible:ring-0 focus-visible:ring-offset-0"
            style={{ maxHeight: 160 }}
          />
          {isStreaming ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={onStop}
              className="size-7 shrink-0"
              aria-label="Arrêter"
              title="Arrêter"
            >
              <Square className="size-3.5" />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon"
              disabled={!input.trim()}
              className="size-7 shrink-0 bg-fabrik-green hover:bg-fabrik-accent-text"
              aria-label="Envoyer"
              title="Envoyer"
            >
              <Send className="size-3.5" />
            </Button>
          )}
        </div>
      </form>

      {showSuggestions && suggestedPrompts.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {suggestedPrompts.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onSuggestedClick?.(p)}
              className="inline-flex items-center gap-1 rounded-fabrik-pill bg-background border border-border px-2.5 py-1 text-[11.5px] text-foreground hover:bg-fabrik-green-soft hover:border-fabrik-green transition-colors"
            >
              {p}
              <ArrowRight className="size-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
